import { useNavigate } from 'react-router-dom';
import config from '../config';

/* ── SVG icons ─────────────────────────────────────────────────────── */
const IconTimer = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" width="32" height="32" className="w-8 h-8 text-parchment-500">
    <circle cx="12" cy="13" r="8" /><path d="M12 9v4l2.5 2.5" /><path d="M9.5 2.5h5" /><path d="M12 2.5V5" />
  </svg>
);
const IconFlame = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" width="32" height="32" className="w-8 h-8 text-parchment-500">
    <path d="M12 2C6.5 7 4 11 4 14a8 8 0 0 0 16 0c0-4-2.5-7-8-12z" /><path d="M12 22v-4" /><path d="M9 18c0-2 1.5-3 3-5 1.5 2 3 3 3 5" />
  </svg>
);
const IconScroll = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" width="32" height="32" className="w-8 h-8 text-parchment-500">
    <path d="M5 3a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2H5z" /><path d="M9 7h6M9 11h6M9 15h4" />
  </svg>
);
const IconBook = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" width="32" height="32" className="w-8 h-8 text-parchment-500">
    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" /><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
  </svg>
);
const IconChart = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" width="32" height="32" className="w-8 h-8 text-parchment-500">
    <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
  </svg>
);
const IconCloud = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" width="32" height="32" className="w-8 h-8 text-parchment-500">
    <path d="M18 10h-1.26A8 8 0 1 0 9 20h9a5 5 0 0 0 0-10z" />
  </svg>
);

/* ── Page ───────────────────────────────────────────────────────────── */
const HomePage = () => {
  const navigate = useNavigate();
  const allowSignup = config.allowSignup;

  return (
    <div
      className="min-h-screen font-serif"
      style={{
        backgroundColor: '#fdfaf3',
        backgroundImage: 'url(/assets/parchment-texture.svg)',
        backgroundSize: '400px 400px',
        backgroundRepeat: 'repeat',
      }}
    >
      {/* ── Nav ── */}
      <header className="container mx-auto px-6 py-6">
        <nav className="flex justify-between items-center">
          <div className="flex items-center gap-3">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" width="28" height="28" className="w-7 h-7 text-ink">
              <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" /><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
            </svg>
            <span className="text-xl font-semibold tracking-wide text-ink">TrackBible</span>
          </div>
          <button
            onClick={() => navigate('/auth')}
            className="text-ink-muted hover:text-ink font-medium px-4 py-2 rounded-md border border-transparent hover:border-parchment-300 hover:bg-parchment-100 transition-all text-sm tracking-wide"
          >
            Sign In
          </button>
        </nav>
      </header>

      {/* ── Hero ── */}
      <main className="container mx-auto px-6">
        <div className="text-center max-w-3xl mx-auto pt-16 pb-24">
          {/* Decorative rule */}
          <div className="flex items-center justify-center gap-4 mb-8">
            <div className="h-px w-16 bg-parchment-400" />
            <span className="text-parchment-500 text-xs tracking-[0.3em] uppercase font-sans">Est. 2026</span>
            <div className="h-px w-16 bg-parchment-400" />
          </div>

          <h1 className="text-5xl md:text-6xl font-bold leading-tight text-ink mb-6">
            Read the entire Bible<br />
            <span className="italic font-medium text-leather-light">one day at a time</span>
          </h1>

          <p className="text-2xl text-ink-light font-medium mb-3">
            At your own pace. No pressure.
          </p>

          {/* Scripture */}
          <p className="text-base italic text-ink-muted mb-12 max-w-md mx-auto leading-relaxed">
            "Your word is a lamp to my feet and a light to my path."
            <span className="not-italic block text-sm mt-1 tracking-wide">— Psalm 119:105</span>
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => navigate('/auth')}
              className="bg-leather text-parchment-100 px-8 py-4 rounded-md text-base font-semibold hover:bg-leather-light transition-all shadow-md tracking-wide"
            >
              {allowSignup ? 'Start Your Journey' : 'Sign In'}
            </button>
            <button
              onClick={() => document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' })}
              className="text-ink border border-parchment-300 bg-parchment-50 px-8 py-4 rounded-md text-base font-medium hover:bg-parchment-100 transition-all tracking-wide"
            >
              Learn More
            </button>
          </div>
        </div>

        {/* ── Divider ── */}
        <div className="flex items-center gap-6 max-w-xl mx-auto mb-24">
          <div className="h-px flex-1 bg-parchment-300" />
          <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20" className="w-5 h-5 text-parchment-400 flex-shrink-0">
            <path d="M12 2l2.4 7.4H22l-6.2 4.5 2.4 7.4L12 17l-6.2 4.3 2.4-7.4L2 9.4h7.6z" />
          </svg>
          <div className="h-px flex-1 bg-parchment-300" />
        </div>

        {/* ── Features ── */}
        <div id="features" className="mb-32">
          <h2 className="text-3xl font-bold text-center text-ink mb-4">Everything you need</h2>
          <p className="text-center text-ink-muted mb-16 font-sans text-sm tracking-wide uppercase">to build a lasting habit</p>
          <div className="grid md:grid-cols-3 gap-6">
            <FeatureCard icon={<IconTimer />} title="Reading Timer" description="Set a timer for however long you have — 10 minutes or an hour. Focus on the Word and let the app handle the clock." />
            <FeatureCard icon={<IconFlame />} title="Daily Streaks" description="Every day you read, your streak grows. Miss a day? Even 5 minutes keeps the chain alive." />
            <FeatureCard icon={<IconScroll />} title="Chapter Progress" description="See exactly where you are in every book. Watch the map of Scripture fill in chapter by chapter." />
            <FeatureCard icon={<IconBook />} title="Reading Plans" description="Chronological, canonical, or one-year — choose the path that fits how you want to encounter the text." />
            <FeatureCard icon={<IconChart />} title="Statistics" description="Honest numbers about your habit: total time, sessions this week, chapters this month." />
            <FeatureCard icon={<IconCloud />} title="Cloud Sync" description="Your progress follows you across every device, saved securely so nothing is ever lost." />
          </div>
        </div>

        {/* ── How It Works ── */}
        <div className="mb-32">
          <h2 className="text-3xl font-bold text-center text-ink mb-4">How it works</h2>
          <p className="text-center text-ink-muted mb-20 font-sans text-sm tracking-wide uppercase">three steps. that's it.</p>
          <div className="grid md:grid-cols-3 gap-12">
            <Step numeral="I"   title="Open the timer"  description="Set it to 15 minutes — or however long you have — and open your Bible." />
            <Step numeral="II"  title="Read"            description="Read at your own pace. The app runs quietly in the background and won't interrupt you." />
            <Step numeral="III" title="Log your chapters" description="When you're done, mark the chapters you read. Your streak updates automatically." />
          </div>
        </div>

        {/* ── CTA ── */}
        <div
          className="mb-32 rounded-2xl p-12 text-center relative overflow-hidden"
          style={{ backgroundColor: '#3b2e1e', backgroundImage: 'url(/assets/parchment-texture.svg)', backgroundSize: '400px 400px', backgroundBlendMode: 'overlay' }}
        >
          <h2 className="text-3xl md:text-4xl font-bold text-parchment-100 mb-4">
            Start your journey today
          </h2>
          <p className="text-parchment-300 mb-8 font-sans max-w-lg mx-auto leading-relaxed">
            The whole Bible. One chapter at a time.<br />
            Build a habit that lasts.
          </p>
          <button
            onClick={() => navigate('/auth')}
            className="bg-parchment-100 text-leather px-8 py-4 rounded-md text-base font-semibold hover:bg-parchment-200 transition-all shadow-lg tracking-wide"
          >
            {allowSignup ? 'Get Started — It\'s Free' : 'Sign In'}
          </button>
        </div>
      </main>

      {/* ── Footer ── */}
      <footer className="container mx-auto px-6 py-12 border-t border-parchment-200">
        <div className="text-center text-ink-muted font-sans text-sm">
          <p className="mb-1 font-serif italic text-base text-ink-light">"Made for those who want to know the Word."</p>
          <p className="text-xs tracking-wide">© 2026 TrackBible</p>
        </div>
      </footer>
    </div>
  );
};

/* ── Sub-components ─────────────────────────────────────────────────── */
const FeatureCard = ({ icon, title, description }: { icon: React.ReactNode; title: string; description: string }) => (
  <div className="bg-parchment-50 border border-parchment-200 p-6 rounded-xl hover:border-parchment-300 hover:shadow-sm transition-all">
    <div className="mb-4">{icon}</div>
    <h3 className="text-lg font-semibold text-ink mb-2">{title}</h3>
    <p className="text-ink-muted font-sans text-sm leading-relaxed">{description}</p>
  </div>
);

const Step = ({ numeral, title, description }: { numeral: string; title: string; description: string }) => (
  <div className="text-center">
    <div className="inline-flex items-center justify-center w-14 h-14 rounded-full border-2 border-parchment-400 text-parchment-500 text-xl font-bold italic mb-5 bg-parchment-50">
      {numeral}
    </div>
    <h3 className="text-xl font-semibold text-ink mb-2">{title}</h3>
    <p className="text-ink-muted font-sans text-sm leading-relaxed">{description}</p>
  </div>
);

export default HomePage;
